import { createContext, type JSX, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export interface PageMeta {
  pageUrl: string;
}

export const PageMetaContext = createContext<PageMeta>({
  pageUrl: '',
});

export const PageMetaProvider = (props: Partial<PageMeta> & { children: ReactNode }): JSX.Element => {
  const [pageUrl, setPageUrl] = useState('');

  useEffect(() => {
    setPageUrl(window?.location.pathname);
  }, []);

  // update pageUrl on client side navigation
  // don't use window.location.pathname, it will use the pathname before the navigation occurs
  // use the url from pushState arguments
  useEffect(() => {
    window.history.pushState = new Proxy(window.history.pushState, {
      apply: (
        target,
        thisArg,
        argArray: [
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data: any,
          unused: string,
          url?: string | URL | null | undefined,
        ],
      ) => {
        const [, , url] = argArray;

        if (!url) {
          return target.apply(thisArg, argArray);
        }

        if (typeof url === 'string') {
          const [, path] = url.split('/');
          setPageUrl(`/${path}`);
        } else {
          setPageUrl(url.pathname);
        }

        return target.apply(thisArg, argArray);
      },
    });
  }, []);

  const values = useMemo(
    () => ({
      pageUrl,
    }),
    [pageUrl],
  );

  return <PageMetaContext.Provider value={values}>{props.children}</PageMetaContext.Provider>;
};

export const usePageMeta = (): PageMeta => {
  const context = useContext(PageMetaContext);

  if (context === undefined) {
    throw new Error('`usePageMeta` hook must be used within a `PageMetaProvider` component');
  }
  return context;
};
